import {
  ref,
  computed,
  provide,
  inject,
  toRef,
  watch,
  onMounted,
  onUnmounted
} from 'vue'
import type { MaybeRef } from 'vue'
import type {
  FieldEntityTarget,
  FieldConfig,
  CustomField
} from '@gat-ship/types'
import { userServiceKey } from '@gat-ship/types/injection-keys'
import { useFieldConfigurationSignalREvents } from '@gat-ship/signalr'
import { useDebounceFn } from '@vueuse/core'
import { useFieldConfigurationApiCalls } from './field-configuration-api-calls'
import { validateCustomFieldValues } from '../model/validate-custom-field-values'
import {
  findConfig,
  fieldProps as resolveFieldProps,
  isSystemFieldVisible as resolveIsSystemFieldVisible,
  resolveVisibleCustomFields
} from '../model/field-resolution'
import type { FieldResolutionContext } from '../model/field-resolution'
import type {
  FieldConfigOverrides,
  CustomFieldsContext,
  SystemFieldRegistration,
  ResolvedCustomField
} from '../model/types'
import { CUSTOM_FIELDS_INJECTION_KEY } from '../model/types'

/**
 * Core composable for the custom fields system.
 * Call this once at the form/view level to load field configurations
 * and provide context to descendant `CustomFieldSlot` and `CustomFieldForm` components.
 */
export function useCustomFields(target: MaybeRef<FieldEntityTarget>) {
  const targetRef = toRef(target)
  const loading = ref(false)
  const fieldConfigs = ref<FieldConfig[]>([])
  const customFieldDefinitions = ref<CustomField[]>([])
  const registeredSystemFields = ref<Map<string, SystemFieldRegistration>>(
    new Map()
  )

  const userService = inject(userServiceKey, undefined)
  const userRoleIds = computed(() => userService?.accessRoleIds.value ?? [])
  const isTenantAdmin = computed(
    () => userService?.isTenantAdmin.value ?? false
  )

  const { getCustomFieldsRequest, getFieldConfigurationsRequest } =
    useFieldConfigurationApiCalls()

  // ── Loading ─────────────────────────────────────────────────────

  async function load() {
    loading.value = true
    try {
      const [customFieldsRes, configsRes] = await Promise.all([
        getCustomFieldsRequest(targetRef.value),
        getFieldConfigurationsRequest(targetRef.value)
      ])
      customFieldDefinitions.value = customFieldsRes.fields
      fieldConfigs.value = configsRes.configurations
    } finally {
      loading.value = false
    }
  }

  // Load on creation and reload when target changes
  load()
  watch(targetRef, () => load())

  // ── Live Reload via SignalR ────────────────────────────────────

  const debouncedLoad = useDebounceFn(load, 300)

  const fieldConfigSignalR = useFieldConfigurationSignalREvents({
    onFieldConfigChanged: (payload) => {
      if (payload.message.entityTarget !== targetRef.value) return
      debouncedLoad()
    },
    onCustomFieldAdded: () => {
      debouncedLoad()
    },
    onCustomFieldUpdated: () => {
      debouncedLoad()
    },
    onCustomFieldRemoved: () => {
      debouncedLoad()
    },
    onCustomFieldsOrderChanged: (payload) => {
      if (payload.message.entityTarget !== targetRef.value) return
      debouncedLoad()
    }
  })

  onMounted(() => {
    fieldConfigSignalR.subscribeToFieldConfigurationEvents()
  })

  onUnmounted(() => {
    fieldConfigSignalR.unsubscribeFromFieldConfigurationEvents()
  })

  // ── Resolution context (unwrap reactive role state, pass as plain data) ─

  function resolutionCtx(): FieldResolutionContext {
    return {
      userRoleIds: userRoleIds.value,
      isTenantAdmin: isTenantAdmin.value
    }
  }

  // ── Public API (delegates to the pure resolution core) ──────────

  /**
   * Returns override props to spread onto a gat-form-field-based input.
   * These props are merged with the developer's defaults (backend config wins).
   */
  function fieldProps(fieldName: string): FieldConfigOverrides {
    return resolveFieldProps(
      findConfig(fieldConfigs.value, fieldName, 'system'),
      resolutionCtx()
    )
  }

  /** Whether a named system field is visible (after resolving config + roles) */
  function isSystemFieldVisible(fieldName: string): boolean {
    return resolveIsSystemFieldVisible(
      fieldConfigs.value,
      fieldName,
      resolutionCtx()
    )
  }

  /** All visible custom fields resolved for rendering */
  const customFields = computed<ResolvedCustomField[]>(() =>
    resolveVisibleCustomFields(
      customFieldDefinitions.value,
      fieldConfigs.value,
      resolutionCtx()
    )
  )

  function registerSystemField(registration: SystemFieldRegistration) {
    registeredSystemFields.value.set(registration.fieldName, registration)
  }

  function unregisterSystemField(fieldName: string) {
    registeredSystemFields.value.delete(fieldName)
  }

  // ── Provide Context ─────────────────────────────────────────────

  const context: CustomFieldsContext = {
    target: targetRef,
    loading,
    fieldConfigs,
    customFieldDefinitions,
    userRoleIds,
    fieldProps,
    isSystemFieldVisible,
    customFields,
    registerSystemField,
    unregisterSystemField
  }

  provide(CUSTOM_FIELDS_INJECTION_KEY, context)

  return {
    loading,
    fieldProps,
    isSystemFieldVisible,
    customFields,
    fieldConfigs,
    customFieldDefinitions,
    /** Reload configurations from the backend */
    reload: load
  }
}

/** Inject the custom fields context (used by CustomFieldSlot and CustomFieldForm) */
export function useCustomFieldsContext() {
  return inject(CUSTOM_FIELDS_INJECTION_KEY, undefined)
}

export { validateCustomFieldValues }
